import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Input } from 'antd';
import { checkDatasetExists } from '@/features/datasets/api/datasetApi';
/**
 * RenameUploadModal - A modal component that allows users to rename their upload
 * when a dataset with the same name already exists.
 * 
 * Features:
 * - Input field for new filename
 * - Real-time validation for filename length and duplicates
 * - Back and Done buttons with proper state management
 * - Consistent styling with the application's design system
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {function} onCancel - Called when user cancels/closes modal
 * @param {function} onBack - Called when user clicks back button
 * @param {function} onDone - Called when user confirms rename with new filename
 * @param {string} originalFileName - Original filename (without extension)
 * @param {string} fileExtension - File extension (e.g., '.csv', '.json')
 * @param {boolean} loading - Shows loading state on buttons
 */
const RenameUploadModal = ({
    visible,
    onCancel,
    onBack,
    onDone,
    originalFileName,
    fileExtension,
    loading = false,
    infoMessage,
}) => {
    const [newFileName, setNewFileName] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [validationError, setValidationError] = useState('');
    const [isValid, setIsValid] = useState(false);
    const inputRef = useRef(null);

    // Reset form when modal opens and focus input
    useEffect(() => {
        if (visible) {
            setNewFileName(originalFileName);
            setValidationError('');
            setIsValid(false);
            // Focus the input after a short delay to ensure modal is fully rendered
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        }
    }, [visible, originalFileName]);

    // Maintain focus when validation errors occur or are cleared
    useEffect(() => {
        if (inputRef.current) {
            // Ensure input stays focused when validation error is set or cleared
            inputRef.current.focus();
        }
    }, [validationError]);

    // Validate filename in real-time
    useEffect(() => {
        if (!visible || !newFileName) {
            setIsValid(false);
            return;
        }

        const validateFileName = async () => {
            const trimmedFileName = newFileName.trim();
            // Check filename length
            if (trimmedFileName.length > 40) {
                setValidationError('File name must be less than 40 characters');
                setIsValid(false);
                return;
            }

            // Check if filename is empty or only whitespace
            if (trimmedFileName.length === 0) {
                setValidationError('File name cannot be empty');
                setIsValid(false);
                return;
            }

            // Check for invalid characters (basic validation)
            // Allow spaces, but we always validate the trimmed final name.
            if (!/^[a-zA-Z0-9 _-]+$/.test(trimmedFileName)) {
                setValidationError('File name can only contain letters, numbers, spaces, underscores, and hyphens');
                setIsValid(false);
                return;
            }

            // Check if filename is the same as original
            if (trimmedFileName === originalFileName) {
                setValidationError('Please enter a different name');
                setIsValid(false);
                return;
            }

            // Check if dataset already exists
            setIsValidating(true);
            try {
                const exists = await checkDatasetExists(trimmedFileName);
                if (exists) {
                    setValidationError('A dataset with this name already exists');
                    setIsValid(false);
                } else {
                    setValidationError('');
                    setIsValid(true);
                }
            } catch (error) {
                console.error('Error checking dataset existence:', error);
                setValidationError('Error checking if name is available');
                setIsValid(false);
            } finally {
                setIsValidating(false);
            }
        };

        // Debounce validation to avoid too many API calls
        const timeoutId = setTimeout(validateFileName, 300);
        return () => clearTimeout(timeoutId);
    }, [newFileName, originalFileName, visible]);

    const handleDone = () => {
        const trimmedFileName = newFileName.trim();
        if (isValid && trimmedFileName) {
            // Ensure final value doesn't start/end with spaces
            if (trimmedFileName !== newFileName) {
                setNewFileName(trimmedFileName);
            }
            onDone(trimmedFileName);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && isValid) {
            handleDone();
        }
    };

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            footer={null}
            centered
            width={520}
            // Keep the overlay above the sticky header/nav (zIndex ~1200-1300)
            zIndex={2000}
            styles={{
                body: {
                    padding: 0,
                },
            }}
            closable={false}
            maskClosable={false}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
            }}>
                {/* Modal Title */}
                <div style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#262626',
                    marginBottom: '16px'
                }}>
                    Rename this upload
                </div>

                {/* Optional info message (e.g., filename too long) */}
                {infoMessage && (
                    <div style={{
                        backgroundColor: '#fff7e6',
                        border: '1px solid #ffd591',
                        color: '#ad6800',
                        fontSize: '12px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        lineHeight: 1.4,
                        width: '100%'
                    }}>
                        {infoMessage}
                    </div>
                )}

                {/* File name label */}
                <div style={{
                    fontSize: '14px',
                    color: '#262626',
                    marginBottom: '8px',
                    fontWeight: 500
                }}>
                    File name
                </div>

                {/* Input field */}
                <Input
                    ref={inputRef}
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter new file name"
                    style={{
                        width: '100%',
                        marginBottom: '8px',
                        borderRadius: '6px',
                        border: validationError ? '1px solid #ff4d4f' : '1px solid #d9d9d9',
                        fontSize: '14px',
                        padding: '8px 12px'
                    }}
                    suffix={isValidating ? (
                        <span style={{ color: '#8c8c8c', fontSize: '12px' }}>Checking...</span>
                    ) : isValid ? (
                        <span style={{ color: '#52c41a', fontSize: '12px' }}>✓</span>
                    ) : null}
                />

                {/* Validation error */}
                {validationError && (
                    <div style={{
                        color: '#ff4d4f',
                        fontSize: '12px',
                        marginBottom: '16px',
                        lineHeight: '1.4'
                    }}>
                        {validationError}
                    </div>
                )}

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    width: '100%'
                }}>
                    <Button
                        onClick={onBack}
                        disabled={loading}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            border: '1px solid #d9d9d9',
                            backgroundColor: '#ffffff',
                            color: '#262626',
                            padding: '0 16px',
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleDone}
                        loading={loading}
                        disabled={loading || !isValid || isValidating}
                    >
                        Done
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RenameUploadModal;
